import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function CGVPage() {
  useDocumentTitle(
    "CGV — Conditions Générales de Vente",
    "Conditions Générales de Vente de SHINRAI CONSULTING : prestations de conseil, formations, accompagnements, modalités, paiement, responsabilité, propriété intellectuelle."
  );
  return (
    <main className="pt-28 pb-24 bg-pearl min-h-screen" data-testid="cgv-page">
      <article className="max-w-3xl mx-auto px-6 sm:px-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-mute hover:text-ink transition mb-10">
          <ArrowLeft size={14} /> Retour à l'accueil
        </Link>

        <div className="font-mono-overline text-mute mb-3">Légal</div>
        <h1 className="font-serif text-5xl leading-tight mb-8">Conditions Générales de Vente</h1>
        <p className="text-mute mb-12">
          SHINRAI CONSULTING — SAS au capital de 104 996,00 € — RCS Nanterre 803 535 269 — SIRET 803 535 269 00010.
          Siège social : 2, Avenue Anatole France, 92110 Clichy. Contact : <a className="underline" href="mailto:info@shinrai.fr">info@shinrai.fr</a>.
          Présentes CGV en vigueur à compter du 17/05/2026, version 2.0.
        </p>

        <section className="space-y-10 text-mute leading-relaxed text-base">

          {/* Lexique */}
          <div>
            <h2 className="font-serif text-2xl text-ink mb-3">Préambule — Définitions</h2>
            <ul className="list-disc pl-6 space-y-1.5 marker:text-gold">
              <li><strong className="text-ink">« CLIENT »</strong> : toute personne physique ou morale, professionnelle, passant commande de prestations auprès de SHINRAI CONSULTING.</li>
              <li><strong className="text-ink">« SHINRAI CONSULTING » / « le Prestataire »</strong> : la société SHINRAI CONSULTING, SAS, RCS Nanterre 803 535 269.</li>
              <li><strong className="text-ink">« CGV »</strong> : les présentes Conditions Générales de Vente.</li>
              <li><strong className="text-ink">« Contrat » / « Lettre de mission »</strong> : l'accord contractuel liant les parties ; en l'absence d'un contrat distinct, la lettre de mission acceptée vaut contrat.</li>
              <li><strong className="text-ink">« Livrables »</strong> : tout document, outil, méthodologie, rapport, support ou élément remis au CLIENT dans le cadre de la prestation.</li>
            </ul>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-ink mb-3">Article 1 — Dispositions générales</h2>
            <p>
              Les présentes CGV constituent, conformément à l'article L441-1 du Code de commerce, le socle unique de la relation
              commerciale entre les parties. Elles ont pour objet de définir les conditions dans lesquelles SHINRAI CONSULTING fournit
              au CLIENT, qui en fait la demande, par voie directe ou via le site shinrai.fr, des prestations de conseil,
              de formation, d'ateliers, de sensibilisation, d'audit, d'accompagnement organisationnel ou d'intervention de toute nature.
            </p>
            <p className="mt-3">
              Toute commande, lettre de mission ou contrat implique, de la part du CLIENT, l'adhésion pleine, entière et sans réserve
              aux présentes CGV, à l'exclusion de tout autre document. Le fait que SHINRAI CONSULTING ne se prévale pas, à un moment donné,
              d'une quelconque clause des présentes CGV ne pourra être interprété comme valant renonciation à s'en prévaloir ultérieurement.
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-ink mb-3">Article 2 — Nature et périmètre des prestations</h2>
            <p>
              SHINRAI CONSULTING propose un accompagnement tri-disciplinaire — Lean Management, neurosciences appliquées et IA opérationnelle —
              visant la mise en place ou l'amélioration de l'organisation, du pilotage et de la performance des entreprises clientes.
              Les prestations comprennent, sans s'y limiter : diagnostics, audits, ateliers, formations, sensibilisations, programmes
              d'acculturation, accompagnements pluri-mois, mise à disposition de méthodologies et d'outils propriétaires.
            </p>
            <p className="mt-3">
              <strong className="text-ink">Périmètre limité aux missions décrites :</strong> seules les prestations expressément stipulées
              dans la lettre de mission ou le devis accepté entrent dans le champ contractuel. Toute prestation complémentaire fera l'objet
              d'un avenant écrit ou d'un nouveau devis.
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-ink mb-3">Article 3 — Devis et commande</h2>
            <p>
              SHINRAI CONSULTING intervient sur demande expresse du CLIENT. Une lettre de mission ou un devis détaillé vaut contrat.
              Y figurent : la nature des prestations, le prix HT, les éventuels rabais et ristournes, les modalités de paiement, le
              rappel de l'adhésion aux CGV.
            </p>
            <p className="mt-3">
              La commande est validée par retour de la lettre de mission ou du devis signé (signature manuscrite ou électronique
              équivalente au sens de l'article 1367 du Code civil), accompagné du règlement de l'acompte demandé. En l'absence de
              retour signé et d'acompte dans le délai de validité indiqué (à défaut, trente (30) jours), la proposition est réputée
              caduque.
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-ink mb-3">Article 4 — Prix</h2>
            <p>
              Les prix sont ceux détaillés dans le devis ou la lettre de mission acceptés par le CLIENT, exprimés en euros et s'entendent
              <strong className="text-ink"> hors taxes</strong> (la TVA applicable étant ajoutée le cas échéant au taux en vigueur lors
              de la facturation). Ils peuvent être forfaitaires, horaires, journaliers ou indexés sur résultat.
            </p>
            <p className="mt-3">
              <strong className="text-ink">Acceptation définitive :</strong> le règlement intégral des honoraires vaut réception et
              acceptation sans réserve des prestations. Aucune renégociation tarifaire ne pourra intervenir postérieurement à la
              réalisation de la prestation.
            </p>
            <p className="mt-3">
              <strong className="text-ink">Révision des prix :</strong> SHINRAI CONSULTING se réserve le droit de réviser ses tarifs
              à tout moment pour toute nouvelle commande. Pour les contrats pluriannuels, les prix peuvent être révisés annuellement
              en fonction de l'inflation (indice INSEE Syntec) avec un préavis de soixante (60) jours.
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-ink mb-3">Article 5 — Modalités de paiement</h2>
            <p>
              Les factures d'acompte et de solde sont payables dès réception par virement bancaire ou, le cas échéant, par carte
              bancaire via la plateforme sécurisée Stripe ou Payhip. Aucun escompte n'est consenti pour paiement anticipé.
            </p>
            <p className="mt-3">
              Le délai de paiement est, par défaut, de trente (30) jours à compter de la date d'émission de la facture, conformément
              à l'article L441-10 du Code de commerce. Aucun délai conventionnel ne pourra dépasser ce plafond.
            </p>
            <p className="mt-3">
              <strong className="text-ink">Acompte (prestations ponctuelles).</strong> Pour toute prestation ponctuelle (audit, atelier,
              sensibilisation, formation, mission ponctuelle), un <strong>acompte de cinquante pour cent (50 %) du montant total HT</strong> est
              exigible à la signature de la lettre de mission. Le solde de cinquante pour cent (50 %) est dû à la livraison du dernier
              livrable ou à la fin de la prestation, selon les modalités précisées au devis.
            </p>
            <p className="mt-3">
              <strong className="text-ink">Prestations à abonnement / accompagnements pluri-mois.</strong> Pour les prestations vendues
              sous forme d'engagement pluri-mois (notamment le parcours DOSI-IA SHINRAI™ et tout programme d'acculturation, de coaching
              ou d'accompagnement à durée déterminée), <strong>l'intégralité (100 %) du montant total HT du contrat devient juridiquement
              exigible à la signature</strong>, le CLIENT étant définitivement engagé pour la totalité de la durée souscrite.
            </p>
            <p className="mt-3">
              <strong>Modalités de règlement de cet engagement :</strong> par facilité, et sauf demande contraire du CLIENT, le règlement
              est réparti en mensualités égales, par <strong>prélèvement automatique mensuel</strong> (SEPA ou carte bancaire récurrente),
              prélevé en mois glissant à compter de la date de signature. La défaillance d'un prélèvement, quelle qu'en soit la cause,
              rend l'intégralité du solde non encore réglé immédiatement et de plein droit exigible, sans mise en demeure préalable.
              Le CLIENT autorise expressément SHINRAI CONSULTING à effectuer les prélèvements mensuels prévus en signant le mandat
              correspondant.
            </p>
            <p className="mt-3">
              Toute interruption anticipée d'un accompagnement pluri-mois ou abonnement, à l'initiative du CLIENT, ne donne lieu à
              aucun remboursement et l'intégralité des mensualités restantes demeure due, sous réserve des dispositions de l'article 7
              applicables aux résiliations.
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-ink mb-3">Article 6 — Retard de paiement</h2>
            <p>Tout retard de paiement entraîne de plein droit et sans qu'il soit besoin de mise en demeure préalable :</p>
            <ul className="list-disc pl-6 mt-2 space-y-1 marker:text-gold">
              <li>l'exigibilité immédiate de toutes les sommes dues au Prestataire, y compris non échues ;</li>
              <li>l'application de pénalités de retard au taux égal à <strong className="text-ink">trois (3) fois le taux d'intérêt légal</strong> en vigueur, calculées sur le montant TTC, courant du lendemain de l'échéance jusqu'à complet paiement, au prorata temporis ;</li>
              <li>une <strong className="text-ink">indemnité forfaitaire pour frais de recouvrement de 40 €</strong> par facture impayée (article D441-5 du Code de commerce), majorée de tous frais supplémentaires justifiés ;</li>
              <li>la suspension immédiate, de plein droit, de toutes prestations en cours et de toute nouvelle commande, sans préavis ni indemnité due au CLIENT ;</li>
              <li>la résiliation de plein droit du contrat, à la seule volonté du Prestataire, quinze (15) jours après une mise en demeure restée sans effet.</li>
            </ul>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-ink mb-3">Article 7 — Durée, résiliation et annulation</h2>
            <p>
              La durée des prestations est définie dans le devis ou la lettre de mission. Chaque partie peut résilier le contrat
              de plein droit en cas de cessation d'activité, cessation de paiement, redressement ou liquidation judiciaire de l'autre
              partie, après mise en demeure restée sans effet pendant un mois.
            </p>
            <p className="mt-3">
              <strong className="text-ink">Résiliation à l'initiative du CLIENT :</strong>
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1 marker:text-gold">
              <li>Pour les <strong>prestations de recrutement</strong> : 90 % du montant total de la prestation engagée et validée demeurent dus.</li>
              <li>Pour les <strong>prestations d'accompagnement pluri-mois et abonnements</strong> (incluant DOSI-IA SHINRAI™, programmes d'acculturation IA, coaching à durée déterminée) : conformément à l'article 5, l'intégralité du montant total HT du contrat est acquise à SHINRAI CONSULTING dès la signature. La résiliation anticipée ne donne lieu à <strong>aucun remboursement</strong>, et l'ensemble des mensualités restantes demeure intégralement dû et immédiatement exigible.</li>
              <li>Pour les <strong>autres prestations ponctuelles</strong> : sont dues l'intégralité des sommes correspondant aux prestations réalisées jusqu'à la date d'effet de la résiliation, l'acompte de 50 % versé restant en tout état de cause acquis au Prestataire, ainsi que les frais et engagements déjà pris auprès de tiers.</li>
            </ul>
            <p className="mt-3">
              <strong className="text-ink">Annulation d'un rendez-vous ou d'une session :</strong> toute action programmée non honorée ni
              décommandée par écrit dans un délai de <strong>soixante-douze (72) heures</strong> précédant sa réalisation reste due en totalité
              au Prestataire. Sa replanification éventuelle sera facturée en sus.
            </p>
            <p className="mt-3">
              <strong className="text-ink">Résiliation à l'initiative du Prestataire :</strong> SHINRAI CONSULTING se réserve le droit de
              résilier toute prestation, de plein droit et sans indemnité due, en cas de manquement grave du CLIENT à ses obligations
              (notamment défaut de paiement, défaut de collaboration, comportement portant atteinte au Prestataire).
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-ink mb-3">Article 8 — Force majeure</h2>
            <p>
              SHINRAI CONSULTING ne pourra être tenue responsable des retards ou impossibilités d'exécution dus à un cas de force majeure
              au sens de l'article 1218 du Code civil, c'est-à-dire un événement échappant au contrôle des parties, qui ne pouvait
              être raisonnablement prévu lors de la conclusion du contrat, et dont les effets ne peuvent être évités par des mesures
              appropriées.
            </p>
            <p className="mt-3">
              Sont notamment, et de manière non limitative, considérés comme cas de force majeure : guerre, invasion, rébellion, blocus,
              sabotage, grève générale, conflit social, intempérie majeure, catastrophe naturelle, incendie, épidémie, pandémie,
              cyberattaque, blocage des transports ou de l'approvisionnement, défaillance des réseaux d'énergie ou de télécommunications,
              acte d'autorité publique.
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-ink mb-3">Article 9 — Obligations des parties — Confidentialité</h2>

            <p className="mt-2"><strong className="text-ink">Obligations du Prestataire.</strong></p>
            <ul className="list-disc pl-6 mt-2 space-y-1 marker:text-gold">
              <li>Réaliser les prestations conformément aux règles de l'art et aux dispositions légales en vigueur.</li>
              <li>Respecter la stricte confidentialité des informations identifiées comme telles par le CLIENT.</li>
              <li>Restituer, à la demande du CLIENT et dans les trente (30) jours suivant la fin de mission, tout document original lui appartenant.</li>
              <li>Signer, à la demande du CLIENT, un accord de confidentialité spécifique.</li>
            </ul>

            <p className="mt-4"><strong className="text-ink">Obligations du CLIENT.</strong></p>
            <ul className="list-disc pl-6 mt-2 space-y-1 marker:text-gold">
              <li>Coopérer activement et de bonne foi avec le Prestataire, fournir dans les délais convenus toutes les informations et documents indispensables à la bonne exécution des prestations.</li>
              <li>Respecter les dates et heures des rendez-vous convenus, sous réserve du préavis de 72 heures (article 7).</li>
              <li>Régler les factures dans les délais contractuels.</li>
              <li>Prendre seul la responsabilité des décisions opérationnelles, stratégiques ou financières qu'il adopte sur la base des analyses et recommandations du Prestataire.</li>
            </ul>

            <p className="mt-4">
              <strong className="text-ink">Confidentialité des méthodologies du Prestataire.</strong> Toute information relative aux
              méthodologies, outils, modèles, savoir-faire, supports, prompts, scripts, processus ou bibliothèques propres à
              SHINRAI CONSULTING — notamment la méthode <strong>SHINRAI™</strong> et le parcours <strong>DOSI-IA SHINRAI™</strong> — est
              considérée comme strictement confidentielle, qu'elle soit ou non explicitement désignée comme telle. Le CLIENT s'engage à ne
              pas les divulguer, copier, modifier ou exploiter à d'autres fins que celles strictement nécessaires à la mission.
            </p>

            <p className="mt-3">
              Les clauses du contrat (objet, prix, modalités) sont confidentielles et ne pourront être communiquées à des tiers non
              autorisés sans accord écrit préalable de l'autre partie.
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-ink mb-3">Article 10 — Propriété intellectuelle</h2>
            <p>
              <strong className="text-ink">Propriété exclusive du Prestataire.</strong> Toutes les méthodologies, outils, modèles, savoir-faire,
              codes sources, supports, livrables-types, frameworks, marques associées (notamment <strong>SHINRAI™</strong>,
              <strong> DOSI-IA SHINRAI™</strong>) développés par SHINRAI CONSULTING avant ou pendant la mission, et ce, même s'ils sont
              adaptés ou intégrés aux Livrables remis au CLIENT, demeurent la propriété intellectuelle exclusive de SHINRAI CONSULTING.
            </p>
            <p className="mt-3">
              <strong className="text-ink">Licence d'usage accordée au CLIENT.</strong> Sous réserve du paiement intégral des honoraires,
              le CLIENT bénéficie d'une licence d'usage personnelle, non exclusive, non cessible et révocable des Livrables, strictement
              limitée à ses besoins internes et à l'objet de la mission. Toute reproduction, diffusion, mise à disposition de tiers,
              commercialisation ou modification est interdite sans autorisation écrite préalable de SHINRAI CONSULTING.
            </p>
            <p className="mt-3">
              <strong className="text-ink">Interdiction de débauche méthodologique.</strong> Le CLIENT s'engage à ne pas exploiter les
              méthodologies et savoir-faire de SHINRAI CONSULTING pour développer, exploiter ou commercialiser, directement ou
              indirectement, une activité concurrente, et ce pendant toute la durée du contrat et pendant vingt-quatre (24) mois
              suivant sa fin.
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-ink mb-3">Article 11 — Responsabilité</h2>
            <p>
              <strong className="text-ink">Obligation de moyens.</strong> SHINRAI CONSULTING est tenue à une obligation de moyens
              dans l'exécution de ses prestations. Elle s'engage à mettre en œuvre les diligences et compétences nécessaires conformément
              aux règles de l'art, sans pour autant garantir un quelconque résultat commercial, financier ou stratégique.
            </p>
            <p className="mt-3">
              <strong className="text-ink">Exclusion de responsabilité.</strong> La responsabilité de SHINRAI CONSULTING ne saurait être
              engagée dans les cas suivants :
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1 marker:text-gold">
              <li>Erreur ou retard résultant d'un manque d'information, d'un défaut de coopération, ou d'informations inexactes communiquées par le CLIENT ou par des tiers.</li>
              <li>Conséquences directes ou indirectes des décisions adoptées par le CLIENT sur la base des analyses ou recommandations du Prestataire.</li>
              <li>Absence de résultats des candidats recrutés (prestations de recrutement).</li>
              <li>Dysfonctionnement, indisponibilité ou défaut de sécurité d'outils ou plateformes tiers utilisés dans le cadre de la prestation.</li>
              <li>Modifications apportées aux Livrables par le CLIENT ou par des tiers postérieurement à leur remise.</li>
              <li>Cas de force majeure (article 8).</li>
            </ul>
            <p className="mt-4">
              <strong className="text-ink">Limitation de responsabilité.</strong> En toute hypothèse, et dans la limite autorisée par
              la loi, la responsabilité de SHINRAI CONSULTING, quel qu'en soit le fondement, est expressément limitée au montant
              total HT effectivement réglé par le CLIENT au titre de la prestation spécifique ayant donné lieu à la réclamation,
              <strong className="text-ink"> sans que ce montant ne puisse excéder, en tout état de cause, la somme de 10 000 € HT par mission.</strong>
            </p>
            <p className="mt-3">
              <strong className="text-ink">Exclusion des dommages indirects.</strong> SHINRAI CONSULTING ne pourra en aucun cas être tenue
              responsable des dommages indirects, immatériels, consécutifs, spéciaux, punitifs ou exemplaires — notamment perte de profits,
              perte de chiffre d'affaires, perte d'image, perte de clientèle, perte de données, interruption d'activité, ou réclamations
              de tiers — quelle qu'en soit la cause.
            </p>
            <p className="mt-3">
              Toute réclamation devra être adressée par lettre recommandée avec accusé de réception dans un délai maximal de trente (30)
              jours suivant la survenance du fait générateur, à peine de forclusion.
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-ink mb-3">Article 12 — Données personnelles et RGPD</h2>
            <p>
              Le CLIENT agit, à l'égard des données personnelles qu'il transmet à SHINRAI CONSULTING ou qu'il fait traiter dans le cadre
              de la mission, en qualité de responsable de traitement au sens du Règlement (UE) 2016/679 (RGPD) et de la loi n° 78-17 du
              6 janvier 1978 modifiée. Il s'engage à respecter l'ensemble de ses obligations légales à ce titre.
            </p>
            <p className="mt-3">
              SHINRAI CONSULTING peut intervenir, selon les cas, en tant que sous-traitant des données personnelles du CLIENT au sens du
              RGPD. Les obligations spécifiques de SHINRAI CONSULTING en tant que sous-traitant feront, lorsque nécessaire, l'objet
              d'un accord de sous-traitance distinct (DPA) ou seront précisées dans la lettre de mission.
            </p>
            <p className="mt-3">
              Les données collectées via le site shinrai.fr sont décrites et encadrées par les
              {" "}<Link to="/mentions-legales" className="underline">mentions légales</Link> et la
              {" "}<Link to="/politique-cookies" className="underline">politique de cookies</Link>.
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-ink mb-3">Article 13 — Non-sollicitation du personnel</h2>
            <p>
              Pendant toute la durée du contrat et pendant <strong className="text-ink">vingt-quatre (24) mois</strong> suivant sa fin,
              le CLIENT s'interdit, directement ou indirectement, de solliciter, débaucher, embaucher ou conclure tout contrat de
              prestation avec un collaborateur, salarié ou consultant indépendant de SHINRAI CONSULTING ayant participé à la mission,
              sauf accord écrit et préalable du Prestataire.
            </p>
            <p className="mt-3">
              En cas de manquement, le CLIENT sera redevable d'une indemnité forfaitaire et libératoire équivalente à <strong className="text-ink">12 mois</strong>
              {" "}de salaire brut ou d'honoraires bruts du collaborateur concerné, sans préjudice du droit du Prestataire de poursuivre
              la réparation intégrale du préjudice subi.
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-ink mb-3">Article 14 — Références commerciales</h2>
            <p>
              Sauf demande contraire expresse du CLIENT formulée par écrit avant le démarrage de la mission, SHINRAI CONSULTING est
              autorisée à mentionner le nom et le logo du CLIENT, ainsi qu'une description générale de la mission réalisée, à des fins
              de références commerciales (site internet, supports de présentation, publications professionnelles).
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-ink mb-3">Article 15 — Droit applicable et juridiction</h2>
            <p>
              Les présentes CGV et la lettre de mission sont régies par le <strong className="text-ink">droit français</strong>. En cas
              de différend relatif à leur interprétation, leur exécution ou leur résolution, les parties s'efforceront en premier lieu
              de trouver une solution amiable.
            </p>
            <p className="mt-3">
              À défaut de résolution amiable, <strong className="text-ink">tout litige sera soumis à la compétence exclusive des tribunaux
              de Paris</strong>, et ce, nonobstant pluralité de défendeurs, demandes incidentes, appels en garantie ou procédure de référé.
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-ink mb-3">Article 16 — Nullité partielle et divisibilité</h2>
            <p>
              Si l'une quelconque des clauses des présentes CGV venait à être déclarée nulle, inapplicable ou non écrite par une
              juridiction compétente, les autres clauses demeureraient pleinement applicables. Les parties s'engagent à négocier de
              bonne foi une clause de remplacement préservant l'équilibre économique du contrat.
            </p>
          </div>

          <div>
            <h2 className="font-serif text-2xl text-ink mb-3">Article 17 — Acceptation des CGV</h2>
            <p>
              Le CLIENT reconnaît avoir pris connaissance des présentes CGV préalablement à toute commande et déclare en accepter
              l'intégralité sans réserve, conformément à l'article L441-1 du Code de commerce. La signature de la lettre de mission,
              le paiement de l'acompte ou tout commencement d'exécution vaut acceptation pleine et entière des présentes CGV.
            </p>
          </div>

        </section>

        <p className="text-xs text-mute mt-12">
          CGV — version 2.0 — applicable à compter du 17/05/2026.
          SHINRAI CONSULTING — SAS · Capital 104 996,00 € · RCS Nanterre 803 535 269.
        </p>
      </article>
    </main>
  );
}
